import { useTranslation } from "react-i18next";
import { Compass, Package, Users, Sparkles, ArrowRight, Check } from "lucide-react";
import { useLanguageNavigation } from "@/components/LanguageRouter";

const services = [
  {
    titleKey: "services.aiDiscovery.title",
    descriptionKey: "services.aiDiscovery.description",
    bulletsKey: "services.aiDiscovery.bullets",
    statKey: "services.aiDiscovery.stat",
    icon: Compass,
    href: "/services/consulting",
  },
  {
    titleKey: "services.fixedPrice.title",
    descriptionKey: "services.fixedPrice.description",
    bulletsKey: "services.fixedPrice.bullets",
    statKey: "services.fixedPrice.stat",
    icon: Package,
    href: "/services/fixed-price",
  },
  {
    titleKey: "services.enablement.title",
    descriptionKey: "services.enablement.description",
    bulletsKey: "services.enablement.bullets",
    statKey: "services.enablement.stat",
    icon: Sparkles,
    href: "/services/enablement",
  },
  {
    titleKey: "services.teams.title",
    descriptionKey: "services.teams.description",
    bulletsKey: "services.teams.bullets",
    statKey: "services.teams.stat",
    icon: Users,
    href: "/services/teams",
  },
];

export function WhatWeBuildSection() {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguageNavigation();

  return (
    <section className="py-24 md:py-32 bg-muted">
      <div className="container">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            {t("whatWeBuild.title")}
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            {t("whatWeBuild.description")}
          </p>
        </div>

        <div className="grid gap-6 sm:grid-cols-2 max-w-[77rem] mx-auto">
          {services.map((service) => {
            const bullets = t(service.bulletsKey, { returnObjects: true }) as string[];
            return (
              <a
                key={service.titleKey}
                href={getLocalizedPath(service.href)}
                className="group relative p-8 rounded-2xl border border-border/50 bg-background hover:border-primary/30 hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-start gap-5">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary transition-all duration-300 group-hover:bg-primary group-hover:text-primary-foreground">
                    <service.icon className="h-6 w-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xl font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                      {t(service.titleKey)}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {t(service.descriptionKey)}
                    </p>
                  </div>
                </div>

                <ul className="mt-5 space-y-2">
                  {Array.isArray(bullets) && bullets.map((bullet, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Check className="h-4 w-4 text-primary shrink-0" />
                      {bullet}
                    </li>
                  ))}
                </ul>

                <div className="mt-5 flex items-center justify-between">
                  <span className="text-sm font-semibold text-primary">
                    {t(service.statKey)}
                  </span>
                  <div className="hidden sm:flex items-center gap-2 text-sm font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    {t("common.learnMore")}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}
